import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import useBrandAsset from "../../../lib/useBrandAsset";

/**
 * Full-bleed editorial hero. Cinematic 70vh on desktop, balanced split on
 * mobile. The image sits on the right with a subtle parallax on scroll;
 * the copy on the left fades-up on mount. No template-y card chrome.
 *
 * Brand brief: "Crafted for moments that stay forever."
 */
export default function LuxuryHero() {
    const imgRef = useRef(null);
    const [y, setY] = useState(0);
    const heroDesktop = useBrandAsset("hero_desktop");
    const heroMobile = useBrandAsset("hero_mobile");

    // Gentle scroll-linked parallax. Capped at 60px so the image never
    // unmoors from the layout grid.
    useEffect(() => {
        const onScroll = () => {
            const offset = Math.min(window.scrollY * 0.18, 60);
            setY(offset);
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <section
            data-testid="shop-hero"
            className="relative -mx-4 sm:-mx-6 lg:-mx-8 mb-8 lg:mb-8 overflow-hidden bg-ivory-soft"
            style={{borderRadius: "1.5em"}}
        >
            {/* Phase 2 (Jul 2026) — COMMERCE-FIRST COMPACT HERO.
                Mobile height 560px → ~55vh so the bestsellers rail peeks into
                fold 1 (products are the argument, per the CRO audit). One CTA
                (the Festive Sale second button competed with it — sale keeps
                its own strip below); practical copy that says WHAT we sell;
                the ★4.9 trust line sits directly under the CTA. */}
            <div className="relative grid lg:grid-cols-[50%_50%] min-h-[55vh] max-h-[560px] sm:min-h-[560px] lg:min-h-[500px] lg:max-h-[820px]">
                {/* === Copy column === */}
                <div
                    className="relative z-10 order-2 lg:order-1 flex flex-col justify-center
                               px-6 sm:px-12 lg:px-6 py-6 sm:py-10 lg:py-0 bg-ivory-soft"
                    data-testid="shop-hero-copy"
                >
                    {/* Overline w/ slow fade */}
                    <p
                        data-testid="shop-hero-overline"
                        className="inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.32em] text-rosegold font-bold mb-3 sm:mb-5 animate-fade-up"
                    >
                        <Sparkles size={12} className="text-rosegold" />
                        {/* "Family-run" (not "Handpicked") — the headline right
                            below already opens with "Handpicked"; the overline
                            should add a NEW trust fact, not echo it */}
                        Family-run Boutique · Est. Anand
                    </p>

                    {/* Headline — plain-spoken, what we are */}
                    <h1
                        data-testid="shop-hero-title"
                        className="font-display text-ink leading-[1.06] sm:leading-[1.02] tracking-tight
                                   text-[1.7rem] sm:text-[3rem] lg:text-[2.5rem] xl:text-[2.5rem]
                                   font-light animate-fade-up"
                        style={{ animationDelay: "120ms" }}
                    >
                        Handpicked Indian wear,<br />
                        <span className="italic text-wine">made for your moments.</span>
                    </h1>

                    <p
                        className="mt-3 sm:mt-6 text-[13.5px] sm:text-base text-ink-soft max-w-md leading-relaxed animate-fade-up"
                        style={{ animationDelay: "200ms" }}
                    >
                        Anarkalis, kurtis, sarees &amp; custom outfits — shipped{" "}
                        <span className="whitespace-nowrap">pan-India</span>, COD available.
                    </p>

                    <div
                        className="mt-5 sm:mt-8 flex flex-wrap items-center gap-2.5 sm:gap-3 animate-fade-up"
                        style={{ animationDelay: "300ms" }}
                    >
                        <Link
                            to="/d/clothing"
                            data-testid="shop-hero-cta"
                            className="group inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-7 rounded-full
                                       bg-ink text-ivory text-[11px] sm:text-[12px] font-bold uppercase tracking-[0.16em] sm:tracking-[0.18em]
                                       hover:bg-wine transition-colors"
                        >
                            Shop the collection
                            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </div>

                    {/* Trust line directly under the CTA — the decision moment */}
                    <p
                        className="mt-4 sm:mt-6 text-[10.5px] sm:text-[11px] text-stone-500 tracking-[0.14em] uppercase animate-fade-up"
                        style={{ animationDelay: "380ms" }}
                    >
                        {/* De-duped vs the subline (which owns "shipped pan-India,
                            COD available") — this line carries proof, not repeats. */}
                        <span className="text-rosegold font-bold">★ 4.9</span> · Loved by 2,300+ customers
                    </p>
                </div>

                {/* === Image column ===
                    Mobile: explicit fixed height so the portrait actually shows
                    (the parent grid alone wasn't constraining it — it collapsed
                    when stacked above the text on phones). Desktop styles
                    untouched per request. */}
                <div className="order-1 lg:order-2 relative overflow-hidden h-[200px] max-h-[480px] lg:h-auto lg:min-h-0 lg:max-h-none">
                    {/* Desktop image with parallax */}
                    <img
                        ref={imgRef}
                        src={heroDesktop}
                        alt="JnS Creation — Festive Edit campaign"
                        className="hidden lg:block absolute inset-0 w-full h-[calc(100%+80px)] object-cover object-[90%_30%] will-change-transform"
                        style={{ transform: `translate3d(0, -${y}px, 0)` }}
                        loading="eager"
                        data-testid="shop-hero-image-desktop"
                    />
                    {/* Mobile portrait — anchored slightly higher so the face leads */}
                    <img
                        src={heroMobile}
                        alt="JnS Creation lookbook"
                        className="lg:hidden absolute inset-0 w-full h-full object-cover object-[47%_38%]"
                        loading="eager"
                        data-testid="shop-hero-image"
                    />

                    {/* Desktop fade — image gradually melts into the copy column */}
                    <div
                        className="hidden lg:block absolute inset-y-0 left-0 w-[55%] pointer-events-none"
                        style={{
                            background:
                                "linear-gradient(to right, #F3EBE1 0%, rgba(243,235,225,0.92) 22%, rgba(243,235,225,0.55) 50%, rgba(243,235,225,0.18) 78%, rgba(243,235,225,0) 100%)",
                        }}
                    />
                    {/* Mobile fade upward into stacked copy */}
                    <div
                        className="lg:hidden absolute inset-x-0 bottom-0 h-2/3 pointer-events-none"
                        style={{
                            background:
                                "linear-gradient(to top, #F3EBE1 0%, rgba(243,235,225,0.92) 22%, rgba(243,235,225,0.55) 50%, rgba(243,235,225,0.18) 78%, rgba(243,235,225,0) 100%)",
                        }}
                    />
                    {/* Soft gold glow corner */}
                    <div
                        className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-40 pointer-events-none hidden lg:block"
                        style={{
                            background:
                                "radial-gradient(circle, rgba(201,164,122,0.42), transparent 70%)",
                        }}
                    />
                </div>
            </div>
        </section>
    );
}
